// --- React & Redux
import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
// --- Actions & Hooks
import { setSessionStart, setAuthToken } from "../../tap/store/auth";
// --- External Libraries
import decode from "jwt-decode";
import moment from "moment";
// --- Utilities
import {
  getAuthToken,
  setAuthToken as setStoredToken,
} from "../../tap/utils/localStorageService";
import { tapApi } from "tap/api.js";

// Refresh token when less than 10 minutes remaining (for 1-hour token lifetime)
const TOKEN_REFRESH_THRESHOLD_MS = 10 * 60 * 1000;
// Check token expiration every 60 seconds
const TOKEN_CHECK_INTERVAL_MS = 60 * 1000;

export const AuthVerify = ({ logOut }) => {
  const dispatch = useDispatch();
  const { sessionStart } = useSelector((s) => s.auth);
  const token = getAuthToken();
  const isRefreshing = useRef(false);
  const checkIntervalRef = useRef(null);

  // Set session start if not set
  useEffect(() => {
    if (!sessionStart) {
      dispatch(setSessionStart(moment.utc().toISOString()));
    }
  }, [sessionStart, dispatch]);

  /**
   * Refreshes the token by calling the extendToken API
   * Updates localStorage and Redux state with the new token
   */
  const refreshToken = useCallback(async () => {
    if (isRefreshing.current || !token) return;

    isRefreshing.current = true;
    try {
      const newToken = await tapApi.extendToken(token);
      if (newToken) {
        // Update localStorage
        setStoredToken(newToken);
        // Update Redux state
        dispatch(setAuthToken(newToken));
        if (import.meta.env.DEV) {
          console.log("Token refreshed successfully");
        }
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
    } finally {
      isRefreshing.current = false;
    }
  }, [token, dispatch]);

  /**
   * Checks if the token is expired or needs refresh
   * - If expired: logs out the user
   * - If within refresh threshold: refreshes the token
   */
  const checkTokenExpiration = useCallback(() => {
    if (!token) return;

    try {
      const decodedJwt = decode(token);
      const expirationTime = decodedJwt.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // Token already expired - log out
      if (timeUntilExpiry <= 0) {
        dispatch(setSessionStart(""));
        logOut();
        return;
      }

      // Token expires soon - refresh it
      if (timeUntilExpiry < TOKEN_REFRESH_THRESHOLD_MS) {
        if (import.meta.env.DEV) {
          const minutesRemaining = Math.round(timeUntilExpiry / 60000);
          console.log(
            `Token expires in ${minutesRemaining} minutes, refreshing...`,
          );
        }
        refreshToken();
      }
    } catch {
      // Invalid token - log out
      dispatch(setSessionStart(""));
      logOut();
    }
  }, [token, dispatch, logOut, refreshToken]);

  // Set up periodic token check
  useEffect(() => {
    if (!token) return;

    // Check immediately on mount and when token changes
    checkTokenExpiration();

    // Set up interval to check periodically
    checkIntervalRef.current = setInterval(
      checkTokenExpiration,
      TOKEN_CHECK_INTERVAL_MS,
    );

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [token, checkTokenExpiration]);

  return null;
};
